import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie, 
  faChartBar, 
  faUsers, 
  faListAlt, 
  faTicketAlt,
  faEllipsisV,
  faUser,
  faUserCheck,
  faUserSlash,
  faFlag,
  faSearch,
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faMapMarkerAlt,
  faGraduationCap,
  faIdCard,
  faTools,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import UserAvatar from '../components/UserAvatar';
import { getCouncilYearOptions, getCurrentCouncilYearStart, isDateInCouncilYear, isLikelyGraduatedBySliitId } from '../utils/leoYear';
import { getMemberQualityFlags } from '../utils/memberQuality';
import usePolling from '../hooks/usePolling';

//
// --- helpers (added) ---
async function safeJson(res) {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch {
    console.error("Non-JSON/invalid JSON from API:", text);
    throw new Error("Server did not return valid JSON");
  }
}

function normalizeMember(m = {}) {
  return {
    id: String(m.SLIITLeoID ?? m.id ?? '').trim(),
    name: m.FullName ?? m.name ?? '',
    regNo: String(m.SLIITID ?? m.regNo ?? '').trim(),
    clubId: String(m.SLIITLeoID ?? m.clubId ?? '').trim(),
    faculty: m.Faculty ?? '',
    center: m.SLIITCenter ?? '',
    year: m.Year ?? '',
    semester: m.Semester ?? '',
    status: m.LeoClubStatus ?? m.status ?? 'Active',
    myLCI: Number(m.MyLCI ?? 0) === 1 ? 1 : 0,
    // MemberBasic's real column is lowercase `role`; the old `m.Role` (capital R)
    // never matched, so every member silently fell back to 'Club Member'.
    role: m.role ?? m.Role ?? 'Club Member',
    registered: m.RegDate ?? '',
    email: m.Email ?? '',
    contact: m.ContactNo ?? '',
    dob: m.DOB ?? '',
    address: m.Address ?? '',
    postal: m.PostalCode ?? '',
    school: m.School ?? '',
    skills: m.Skills ?? '',
    nic: m.NIC ?? '',
    gender: m.Gender ?? '',
    AccountBlock: Number(m.AccountBlock ?? 0),
    // MemberBasic has no `Image` column -- the real uploaded-photo column
    // is `ProfileLink`, so this always came back blank before.
    image: m.ProfileLink ?? m.Image ?? ''
  };
}
// --- end helpers ---

function MemberDatabase() {
  // State management
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [dropdownMember, setDropdownMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [facultyFilter, setFacultyFilter] = useState('');
  const [yearLevelFilter, setYearLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [councilYearFilter, setCouncilYearFilter] = useState('');
  const [graduatedFilter, setGraduatedFilter] = useState(''); // '' | 'yes' | 'no'
  const [suspiciousFilter, setSuspiciousFilter] = useState(''); // '' | 'flagged' | 'clean'
  const [restrictedFilter, setRestrictedFilter] = useState(''); // '' | 'restricted' | 'active'
  const [deletingId, setDeletingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null); // kept as-is

  // Notification system
  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 
      type === 'warning' ? 'bg-yellow-500' : 
      'bg-blue-500'
    }`;
    notification.textContent = message;
    
    // Add to DOM
    document.body.appendChild(notification);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transition = 'opacity 0.5s ease';
      
      // Remove from DOM after fade out
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 500);
    }, 5000);
  };
  
  // Open member profile modal
  const openMemberProfile = (member) => {
    setViewingMember(member);
    setIsModalOpen(true);
  };
  
  // Close member profile modal
  const closeModal = () => {
    setIsModalOpen(false);
    setViewingMember(null);
  };

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  // Fetch members data (replaced with robust version). `silent` skips the
  // loading/error UI so the background poll below doesn't flash the whole
  // page every refresh.
  const fetchMembers = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      // Explicit limit: the endpoint defaults to 4000 rows with none
      // passed, silently truncating the real roster (4085+ members).
      const response = await fetch('https://dev.sliitleo.org/Backend/member_api.php?action=list_basic&limit=40000', {
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const raw = await safeJson(response);

      const rows =
        Array.isArray(raw) ? raw :
        Array.isArray(raw.data) ? raw.data :
        Array.isArray(raw.members) ? raw.members :
        null;

      if (!rows) {
        console.error("Unexpected payload shape:", raw);
        throw new Error("Unexpected response payload from server");
      }

      setMembers(rows.map(normalizeMember));
    } catch (err) {
      console.error('Error fetching members:', err);
      if (!silent) setError('Failed to load members. Please try again later.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // Live-refresh the roster in the background (new members, restrictions,
  // deletions elsewhere, etc.) without needing a manual reload.
  usePolling(() => fetchMembers(true), { intervalMs: 45000, hiddenIntervalMs: 120000 });

  // Helper to calculate age from DOB string (YYYY-MM-DD or similar)
  const calculateAge = (dob) => {
    if (!dob) return '';
    const dobDate = new Date(dob);
    if (isNaN(dobDate)) return '';
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return age;
  };

  // ---- Filter bar support -------------------------------------------------
  // Council (Leostic) year options for the dropdown -- July 1 - June 30,
  // same definition the Club Dashboard uses, from utils/leoYear so the two
  // pages never disagree about where a year boundary falls.
  const councilYearOptions = useMemo(() => getCouncilYearOptions(), []);
  const currentCouncilYearStart = getCurrentCouncilYearStart();

  // Adds `flagged`/`flags` (data-quality heuristic, utils/memberQuality) and
  // `graduated` (SLIIT-ID-based estimate, utils/leoYear) to every member once
  // up front, rather than recomputing them inside the filter/render loop.
  // This is also what finally gives the pre-existing `member.flagged` check
  // on the mobile card border (below) a real value instead of always-undefined.
  const enrichedMembers = useMemo(() => {
    const allSliitIds = members.map((m) => m.regNo).filter(Boolean);
    return members.map((member) => {
      const flags = getMemberQualityFlags(
        {
          FullName: member.name,
          SLIITID: member.regNo,
          RegDate: member.registered,
          Email: member.email,
          ContactNo: member.contact,
          NIC: member.nic,
        },
        allSliitIds
      );
      return {
        ...member,
        flags,
        flagged: flags.length > 0,
        graduated: isLikelyGraduatedBySliitId(member.regNo, currentCouncilYearStart),
      };
    });
  }, [members, currentCouncilYearStart]);

  // Dropdown options derived from whatever values actually exist in the
  // roster, so this never drifts out of sync with real data the way a
  // hand-typed list would.
  const facultyOptions = useMemo(() => [...new Set(enrichedMembers.map((m) => m.faculty).filter(Boolean))].sort(), [enrichedMembers]);
  const yearLevelOptions = useMemo(() => [...new Set(enrichedMembers.map((m) => m.year).filter(Boolean))].sort(), [enrichedMembers]);
  const statusOptions = useMemo(() => [...new Set(enrichedMembers.map((m) => m.status).filter(Boolean))].sort(), [enrichedMembers]);
  const roleOptions = useMemo(() => [...new Set(enrichedMembers.map((m) => m.role).filter(Boolean))].sort(), [enrichedMembers]);

  const activeFilterCount = [facultyFilter, yearLevelFilter, statusFilter, roleFilter, councilYearFilter, graduatedFilter, suspiciousFilter, restrictedFilter].filter(Boolean).length;
  const clearAllFilters = () => {
    setFacultyFilter('');
    setYearLevelFilter('');
    setStatusFilter('');
    setRoleFilter('');
    setCouncilYearFilter('');
    setGraduatedFilter('');
    setSuspiciousFilter('');
    setRestrictedFilter('');
    setSearch('');
    setPage(1);
  };

  // Filter members based on search + the filter bar (made safe)
  const filteredMembers = enrichedMembers.filter(member => {
    const s = search.toLowerCase();
    const matchesSearch = (
      (member.name || '').toLowerCase().includes(s) ||
      String(member.regNo || '').toLowerCase().includes(s) ||
      String(member.clubId || '').toLowerCase().includes(s)
    );
    if (!matchesSearch) return false;

    if (facultyFilter && member.faculty !== facultyFilter) return false;
    if (yearLevelFilter && member.year !== yearLevelFilter) return false;
    if (statusFilter && member.status !== statusFilter) return false;
    if (roleFilter && member.role !== roleFilter) return false;
    if (councilYearFilter && !isDateInCouncilYear(member.registered, Number(councilYearFilter))) return false;
    if (graduatedFilter === 'yes' && !member.graduated) return false;
    if (graduatedFilter === 'no' && member.graduated) return false;
    if (suspiciousFilter === 'flagged' && !member.flagged) return false;
    if (suspiciousFilter === 'clean' && member.flagged) return false;
    if (restrictedFilter === 'restricted' && !member.AccountBlock) return false;
    if (restrictedFilter === 'active' && member.AccountBlock) return false;

    return true;
  });

  // Pagination logic
  const pageCount = Math.ceil(filteredMembers.length / rowsPerPage);
  const paginatedMembers = filteredMembers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Toggle member selection
  const toggleMember = (SLIITLeoID) => {
    setSelectedMember(selectedMember === SLIITLeoID ? null : SLIITLeoID);
    setDropdownMember(null); // Close dropdown when opening details
  };

  // Toggle MYLCI status for a member (use normalized id)
  const toggleMyLCI = async (SLIITLeoID, currentStatus) => {
    try {
      const newStatus = currentStatus ? 0 : 1; // Toggle between 0 and 1
      
      // Optimistic UI update
      setMembers(members => members.map(member => 
        member.id === SLIITLeoID 
          ? { ...member, myLCI: newStatus, updating: true } 
          : member
      ));

      // Update in database
      const response = await fetch('https://dev.sliitleo.org/Backend/member_api.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          SLIITLeoID: SLIITLeoID,
          MyLCI: newStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update MYLCI status');
      }

      // Update local state with response data
      setMembers(members => members.map(member => 
        member.id === SLIITLeoID 
          ? { ...member, updating: false }
          : member
      ));
      
      // Close dropdown
      setDropdownMember(null);
      
    } catch (error) {
      console.error('Error updating MYLCI status:', error);
      // Revert optimistic update on error
      setMembers(members => members.map(member => 
        member.id === SLIITLeoID 
          ? { ...member, myLCI: currentStatus, updating: false }
          : member
      ));
      alert('Failed to update MYLCI status. Please try again.');
    }
  };

  // Permanently delete a member. Only ever reachable from the UI when the
  // member is already restricted (AccountBlock=1) -- delete_member.php
  // re-verifies that server-side too, so this can't be bypassed by calling
  // the endpoint directly.
  const handleDeleteMember = async (member) => {
    if (!member.AccountBlock) {
      showNotification('Restrict this member\'s login before deleting them.', 'warning');
      return;
    }
    const confirmed = window.confirm(
      `Permanently delete ${member.name || 'this member'} (${member.regNo || member.id})?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(member.id);
    try {
      const response = await fetch('https://dev.sliitleo.org/Backend/delete_member.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ sliitleo_id: member.id }),
      });
      const result = await safeJson(response);
      if (!response.ok || !result.success) {
        throw new Error(result.message || `Server error (${response.status})`);
      }
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      showNotification('Member deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting member:', error);
      showNotification(error.message || 'Failed to delete member. Please try again.', 'error');
    } finally {
      setDeletingId(null);
      setDropdownMember(null);
    }
  };

  // Toggle dropdown
  const toggleDropdown = (memberId, e) => {
    e.stopPropagation();
    setDropdownMember(dropdownMember === memberId ? null : memberId);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 ml-0 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="panel flex flex-col items-center justify-center gap-3" style={{ padding: 'var(--sp-8)', minHeight: 220 }}>
            <div className="spinner lg" role="status" aria-label="Loading members" />
            <p style={{ color: 'var(--text-2)', fontSize: 13 }}>Loading members…</p>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 ml-0 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
          <div className="error-banner" style={{ padding: 'var(--sp-5)', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary btn-sm"
              style={{ marginTop: 'var(--sp-3)' }}
            >
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 ml-0 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="panel page-head" style={{ padding: 'var(--sp-4) var(--sp-5)', alignItems: 'center' }}>
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <span
              className="icon-btn"
              aria-hidden="true"
              style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none' }}
            >
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: 16 }} />
            </span>
            <div className="min-w-0">
              <h1 className="truncate">Member Database</h1>
              <p className="page-desc truncate">Manage and view all members</p>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 mt-4">
            <Link
              to="/club-statistics"
              className="panel"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--text-1)', textDecoration: 'none' }}
            >
              <span className="icon-btn" aria-hidden="true">
                <FontAwesomeIcon icon={faChartPie} style={{ fontSize: 14 }} />
              </span>
              Club Statistics
            </Link>
            <Link
              to="/reporting-hub"
              className="panel"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--text-1)', textDecoration: 'none' }}
            >
              <span className="icon-btn" aria-hidden="true">
                <FontAwesomeIcon icon={faChartBar} style={{ fontSize: 14 }} />
              </span>
              Reporting Hub
            </Link>
            <Link
              to="/member-database"
              className="panel"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textDecoration: 'none', borderColor: 'var(--accent)', background: 'rgba(124,58,237,0.14)' }}
            >
              <span className="icon-btn" aria-hidden="true" style={{ color: 'var(--accent)' }}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: 14 }} />
              </span>
              Member Database
            </Link>
            <Link
              to="/category-selection"
              className="panel"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--text-1)', textDecoration: 'none' }}
            >
              <span className="icon-btn" aria-hidden="true">
                <FontAwesomeIcon icon={faListAlt} style={{ fontSize: 14 }} />
              </span>
              Category Selections
            </Link>
            <Link
              to="/tickets-admin"
              className="panel"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--text-1)', textDecoration: 'none' }}
            >
              <span className="icon-btn" aria-hidden="true">
                <FontAwesomeIcon icon={faTicketAlt} style={{ fontSize: 14 }} />
              </span>
              Tickets Admin
            </Link>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <label htmlFor="member-search" className="sr-only">Search members by name, ID, or club ID</label>
          <div
            className="field"
            style={{ margin: 0, flexDirection: 'row', alignItems: 'center', gap: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '0 12px' }}
          >
            <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--text-3)', fontSize: 14, flexShrink: 0 }} />
            <input
              id="member-search"
              type="text"
              placeholder="Search members by name, ID, or club ID..."
              aria-label="Search members by name, ID, or club ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', background: 'transparent', padding: '10px 0' }}
            />
          </div>
        </div>

        {/* Filter Bar */}
        <div className="panel" style={{ padding: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 'var(--sp-3)', flexWrap: 'wrap', gap: 8 }}>
            <span className="eyebrow">Filters{activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}</span>
            {activeFilterCount > 0 && (
              <button type="button" onClick={clearAllFilters} className="btn btn-ghost btn-sm">
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 11 }} /> Clear all
              </button>
            )}
          </div>
          <div className="filter-row" style={{ flexWrap: 'wrap', gap: 10 }}>
            <select
              value={facultyFilter}
              onChange={(e) => { setFacultyFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by faculty"
            >
              <option value="">All Faculties</option>
              {facultyOptions.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>

            <select
              value={yearLevelFilter}
              onChange={(e) => { setYearLevelFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by student year level"
            >
              <option value="">All Year Levels</option>
              {yearLevelOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by Leo club status"
            >
              <option value="">All Statuses</option>
              {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by role"
            >
              <option value="">All Roles</option>
              {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>

            <select
              value={councilYearFilter}
              onChange={(e) => { setCouncilYearFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by Leostic (council) year joined"
            >
              <option value="">All Leostic Years</option>
              {[...councilYearOptions].reverse().map(({ startYear, label }) => (
                <option key={startYear} value={String(startYear)}>
                  {label}{startYear === currentCouncilYearStart ? ' (current)' : ''}
                </option>
              ))}
            </select>

            <select
              value={graduatedFilter}
              onChange={(e) => { setGraduatedFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by likely graduation status"
              title="Estimated from SLIIT ID enrollment year + 4"
            >
              <option value="">Graduated: Any</option>
              <option value="yes">Likely Graduated</option>
              <option value="no">Not Graduated</option>
            </select>

            <select
              value={suspiciousFilter}
              onChange={(e) => { setSuspiciousFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by data-quality flag"
              title="Heuristic checks: placeholder-looking names, malformed/duplicate SLIIT IDs, future dates, missing contact info"
            >
              <option value="">Data Quality: Any</option>
              <option value="flagged">Flagged only</option>
              <option value="clean">Clean only</option>
            </select>

            <select
              value={restrictedFilter}
              onChange={(e) => { setRestrictedFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto' }}
              aria-label="Filter by login-restriction status"
            >
              <option value="">Login Access: Any</option>
              <option value="restricted">Restricted only</option>
              <option value="active">Not restricted</option>
            </select>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {paginatedMembers.length > 0 ? (
            paginatedMembers.map((member) => (
              <div
                key={member.id}
                className="panel"
                style={{
                  padding: 'var(--sp-4)',
                  borderColor: member.flagged ? 'var(--error)' : 'var(--border)',
                  boxShadow: selectedMember === member.id ? '0 0 0 2px var(--link)' : 'none',
                }}
                onClick={() => toggleMember(member.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0">
                    <UserAvatar src={member.image} alt={member.name || 'Member'} size={44} ring="var(--surface-1)" fallback="/user.png" />
                    <div className="min-w-0">
                      <h3 className="truncate" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{member.name}</h3>
                      <p className="num truncate" style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        {member.regNo}
                        {member.graduated && <span style={{ color: 'var(--success)', marginLeft: 6 }}>· Grad</span>}
                        {member.flagged && <span style={{ color: 'var(--warning)', marginLeft: 6 }}>· Flagged</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`pill ${member.myLCI ? 'pill-success' : 'pill-info'}`}>
                      <span className="dot" />
                      {member.myLCI ? 'MYLCI' : 'Non-MYLCI'}
                    </span>
                    <button
                      className="icon-btn"
                      aria-label={`More actions for ${member.name || 'member'}`}
                      aria-haspopup="menu"
                      aria-expanded={dropdownMember === member.id}
                      onClick={(e) => toggleDropdown(member.id, e)}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: 14 }} />
                    </button>
                  </div>
                </div>

                {dropdownMember === member.id && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMember(member.id);
                        }}
                        className="btn btn-ghost btn-sm"
                        style={{ flex: 1, justifyContent: 'center', color: 'var(--link)' }}
                      >
                        <FontAwesomeIcon icon={faUser} style={{ fontSize: 12 }} />
                        View
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--success)' }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 12 }} />
                        Message
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--accent)' }}>
                        <FontAwesomeIcon icon={faTools} style={{ fontSize: 12 }} />
                        Actions
                      </button>
                    </div>
                  </div>
                )}

                {selectedMember === member.id && (
                  <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-start space-x-2">
                        <span style={{ color: 'var(--text-3)', marginTop: 2 }}>
                          <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 12 }} />
                        </span>
                        <div className="min-w-0">
                          <p style={{ fontSize: 11, color: 'var(--text-3)' }}>Email</p>
                          <p className="truncate" style={{ color: 'var(--text-1)' }} title={member.email || ''}>{member.email || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span style={{ color: 'var(--text-3)', marginTop: 2 }}>
                          <FontAwesomeIcon icon={faPhone} style={{ fontSize: 12 }} />
                        </span>
                        <div className="min-w-0">
                          <p style={{ fontSize: 11, color: 'var(--text-3)' }}>Contact</p>
                          <p className="num" style={{ color: 'var(--text-1)' }}>{member.contact || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span style={{ color: 'var(--text-3)', marginTop: 2 }}>
                          <FontAwesomeIcon icon={faGraduationCap} style={{ fontSize: 12 }} />
                        </span>
                        <div className="min-w-0">
                          <p style={{ fontSize: 11, color: 'var(--text-3)' }}>Faculty</p>
                          <p className="truncate" style={{ color: 'var(--text-1)' }}>{member.faculty || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span style={{ color: 'var(--text-3)', marginTop: 2 }}>
                          <FontAwesomeIcon icon={faIdCard} style={{ fontSize: 12 }} />
                        </span>
                        <div className="min-w-0">
                          <p style={{ fontSize: 11, color: 'var(--text-3)' }}>Role</p>
                          <p className="truncate" style={{ color: 'var(--text-1)' }}>{member.role || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No members found matching your search.</p>
            </div>
          )}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto panel">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10" style={{ background: 'var(--surface-1)' }}>
              <tr>
                <th>Member</th>
                <th>ID</th>
                <th>Status</th>
                <th>Faculty</th>
                <th>Role</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMembers.length > 0 ? (
                paginatedMembers.map((member) => (
                  <React.Fragment key={member.id}>
                    <tr
                      className="cursor-pointer"
                      style={{ background: selectedMember === member.id ? 'var(--surface-2)' : 'transparent' }}
                      onClick={() => toggleMember(member.id)}
                    >
                      <td className="whitespace-nowrap">
                        <div className="flex items-center min-w-0">
                          <UserAvatar src={member.image} alt={member.name || 'Member'} size={40} ring="var(--surface-1)" fallback="/user.png" />
                          <div className="ml-4 min-w-0">
                            <div className="truncate" style={{ fontWeight: 600, color: 'var(--text-1)', maxWidth: 220 }} title={member.name}>
                              {member.name}
                            </div>
                            <div className="truncate" style={{ fontSize: 12, color: 'var(--text-3)', maxWidth: 220 }} title={member.email || 'No email'}>
                              {member.email || 'No email'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="flex items-center" style={{ gap: 6 }}>
                          <div className="num" style={{ color: 'var(--text-2)' }}>{member.regNo}</div>
                          {member.graduated && (
                            <span className="pill pill-success" style={{ fontSize: 10, padding: '2px 6px' }} title="Estimated from SLIIT ID enrollment year + 4">
                              Grad
                            </span>
                          )}
                        </div>
                        <div className="num" style={{ fontSize: 11, color: 'var(--text-3)' }}>LCS{member.clubId}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        <span className={`pill ${member.status === 'Active' ? 'pill-success' : 'pill-info'}`}>
                          <span className="dot" />
                          {member.status || 'Inactive'}
                        </span>
                        <div className="mt-1" style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {member.myLCI ? 'MYLCI Member' : 'Non-MYLCI'}
                        </div>
                        {member.flagged && (
                          <div className="mt-1" style={{ fontSize: 11, color: 'var(--warning)' }} title={member.flags.join('; ')}>
                            <FontAwesomeIcon icon={faFlag} style={{ fontSize: 10, marginRight: 4 }} />
                            Review
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap" style={{ color: 'var(--text-2)' }}>
                        {member.faculty || 'N/A'}
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {member.year || '?'} S{member.semester || '?'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap" style={{ color: 'var(--text-2)' }}>
                        {member.role || 'Member'}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                          {/* <button
                            className="text-blue-400 hover:text-blue-300 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              // View action
                            }}
                          >
                            <FontAwesomeIcon icon={faUser} />
                          </button> */}
                          <button
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (member.email) {
                                window.location.href = `mailto:${member.email}?subject=Message from Leo Club Portal`;
                              } else {
                                showNotification('No email address available for this member', 'warning');
                              }
                            }}
                            title="Send email to this member"
                            aria-label={`Email ${member.name || 'member'}`}
                          >
                            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 14 }} />
                          </button>
                          <div className="relative">
                            <button
                              className="icon-btn"
                              onClick={(e) => toggleDropdown(member.id, e)}
                              aria-label={`More actions for ${member.name || 'member'}`}
                              aria-haspopup="menu"
                              aria-expanded={dropdownMember === member.id}
                            >
                              <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: 14 }} />
                            </button>
                            {dropdownMember === member.id && (
                              <div className="panel absolute right-0 mt-2 w-48 py-1 z-20" role="menu">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMyLCI(member.id, member.myLCI);
                                  }}
                                  disabled={member.updating}
                                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2"
                                  style={{ color: 'var(--text-2)' }}
                                >
                                  <FontAwesomeIcon
                                    icon={member.myLCI ? faUserSlash : faUserCheck}
                                    style={{ color: member.myLCI ? 'var(--warning)' : 'var(--success)' }}
                                  />
                                  {member.myLCI ? 'Remove from MYLCI' : 'Add to MYLCI'}
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const loadingButton = e.currentTarget;
                                    const originalText = loadingButton.innerHTML;
                                    
                                    try {
                                      // Show loading state
                                      loadingButton.disabled = true;
                                      loadingButton.innerHTML = `
                                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        ${member.AccountBlock ? 'Unrestricting...' : 'Restricting...'}
                                      `;

                                      const response = await fetch('https://dev.sliitleo.org/Backend/toggle_restrict_login.php', {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Accept': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          sliitleo_id: member.id,                  // fixed: use normalized id
                                          current_status: member.AccountBlock || 0
                                        })
                                      });
                                      
                                      const result = await response.json();
                                      
                                      if (!response.ok) {
                                        throw new Error(result.message || 'Server error');
                                      }

                                      if (result.success) {
                                        // Update the member's AccountBlock status in the local state
                                        const updatedMembers = members.map(m => 
                                          m.id === member.id 
                                            ? { ...m, AccountBlock: result.new_status } 
                                            : m
                                        );
                                        setMembers(updatedMembers);
                                        
                                        // Show success toast/notification
                                        showNotification(
                                          `User login ${result.new_status ? 'restricted' : 'unrestricted'} successfully`,
                                          result.new_status ? 'success' : 'info'
                                        );
                                      } else {
                                        throw new Error(result.message || 'Failed to update login status');
                                      }
                                    } catch (error) {
                                      console.error('Error toggling login restriction:', error);
                                      showNotification(
                                        error.message || 'An error occurred while updating login status',
                                        'error'
                                      );
                                    } finally {
                                      // Reset button state
                                      if (loadingButton) {
                                        loadingButton.disabled = false;
                                        loadingButton.innerHTML = originalText;
                                      }
                                      // close the same dropdown state used everywhere else
                                      setDropdownMember(null);
                                    }
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2"
                                  style={{ color: member.AccountBlock ? 'var(--success)' : 'var(--warning)' }}
                                >
                                  <FontAwesomeIcon
                                    icon={member.AccountBlock ? faUserCheck : faUserSlash}
                                    style={{ color: member.AccountBlock ? 'var(--success)' : 'var(--warning)' }}
                                  />
                                  {member.AccountBlock ? 'Unrestrict Login' : 'Restrict Login'}
                                </button>
                                <button className="w-full text-left px-4 py-2 text-sm flex items-center gap-2" style={{ color: 'var(--error)' }}>
                                  <FontAwesomeIcon icon={faFlag} />
                                  Flag Member
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMember(member);
                                  }}
                                  disabled={!member.AccountBlock || deletingId === member.id}
                                  title={member.AccountBlock ? 'Permanently delete this member' : "Restrict this member's login first to enable delete"}
                                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2"
                                  style={{ color: 'var(--error)', borderTop: '1px solid var(--border)' }}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  {deletingId === member.id ? 'Deleting…' : 'Delete Member'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {selectedMember === member.id && (
                      <tr style={{ background: 'var(--surface-2)' }}>
                        <td colSpan="6" className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-2)' }}>Contact Information</h4>
                              <div className="space-y-2">
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faEnvelope} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div className="min-w-0">
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Email</p>
                                    <p className="text-sm truncate" style={{ color: 'var(--text-1)' }} title={member.email || ''}>{member.email || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faPhone} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div>
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Phone</p>
                                    <p className="text-sm num" style={{ color: 'var(--text-1)' }}>{member.contact || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-2)' }}>Personal Details</h4>
                              <div className="space-y-2">
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faCalendarAlt} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div>
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Date of Birth</p>
                                    <p className="text-sm" style={{ color: 'var(--text-1)' }}>
                                      {member.dob ? `${formatDate(member.dob)} (${calculateAge(member.dob)} years)` : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div>
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Address</p>
                                    <p className="text-sm" style={{ color: 'var(--text-1)' }}>
                                      {member.address ? `${member.address}, ${member.postal}` : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faUser} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div>
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>NIC</p>
                                    <p className="text-sm num" style={{ color: 'var(--text-1)' }}>
                                      {member.nic ? `${member.nic}` : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-2)' }}>Education</h4>
                              <div className="space-y-2">
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faGraduationCap} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div>
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>School</p>
                                    <p className="text-sm" style={{ color: 'var(--text-1)' }}>{member.school || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <FontAwesomeIcon icon={faTools} className="mt-0.5 mr-2 flex-shrink-0" style={{ color: 'var(--text-3)' }} />
                                  <div>
                                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Skills</p>
                                    <p className="text-sm" style={{ color: 'var(--text-1)' }}>{member.skills || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                            <div className="flex items-center justify-between">
                              <div className="text-xs" style={{ color: 'var(--text-3)' }}>
                                Member since: {member.registered ? formatDate(member.registered) : 'N/A'}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMemberProfile(member);
                                  }}
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: 'var(--link)' }}
                                >
                                  View Full Profile
                                </button>
                                <a
                                  href={`https://wa.me/+94${member.contact ? member.contact.replace(/\D/g, '') : ''}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-ghost btn-sm"
                                  style={{ color: 'var(--success)' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  WhatsApp
                                </a>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <div className="empty-state" style={{ padding: 'var(--sp-4) 0' }}>
                      <p>No members found matching your search.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm" style={{ color: 'var(--text-2)' }}>
              Showing {Math.min((page - 1) * rowsPerPage + 1, filteredMembers.length)}-{
                Math.min(page * rowsPerPage, filteredMembers.length)
              } of {filteredMembers.length} members
            </div>
            <div className="flex items-center gap-2">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                &larr; Previous
              </button>
              <span className="num whitespace-nowrap" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                Page {page} of {pageCount}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
              >
                Next &rarr;
              </button>
            </div>
          </div>
        )}
        {/* Member Profile Modal */}
        {isModalOpen && viewingMember && (
      <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Member profile">
        <div className="modal-panel lg panel">
          {/* Modal Header */}
          <div className="modal-head">
            <h3>Member Profile</h3>
            <button
              onClick={closeModal}
              className="icon-btn"
              aria-label="Close member profile"
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Column - Profile */}
              <div className="md:w-1/3">
                <div className="panel text-center" style={{ padding: 'var(--sp-4)' }}>
                  <div className="mx-auto mb-4" style={{ width: 96, height: 96 }}>
                    <UserAvatar src={viewingMember.image} alt={viewingMember.name || 'Member'} size={96} ring="var(--surface-1)" fallback="/user.png" />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-1)' }}>{viewingMember.name}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-2)' }}>{viewingMember.role || 'Member'}</p>

                  <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="flex items-center justify-center space-x-2">
                      <span className={`pill ${viewingMember.myLCI ? 'pill-success' : 'pill-info'}`}>
                        <span className="dot" />
                        {viewingMember.myLCI ? 'MYLCI Member' : 'Non-MYLCI'}
                      </span>
                    </div>
                    <p className="text-xs mt-2" style={{ color: 'var(--text-3)' }}>
                      Member since: {viewingMember.registered ? formatDate(viewingMember.registered) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column - Details */}
              <div className="md:w-2/3">
                <h4 className="mb-4" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="panel" style={{ padding: 'var(--sp-4)' }}>
                    <h5 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
                      <FontAwesomeIcon icon={faIdCard} />
                      ID & Contact
                    </h5>
                    <div className="space-y-2">
                      <p className="text-sm">
                        <span style={{ color: 'var(--text-3)' }}>ID:</span> <span className="num">{viewingMember.regNo || 'N/A'}</span>
                      </p>
                      <p className="text-sm">
                        <span style={{ color: 'var(--text-3)' }}>Club ID:</span> <span className="num">LCS{viewingMember.clubId || 'N/A'}</span>
                      </p>
                      <p className="text-sm truncate" title={viewingMember.email || ''}>
                        <span style={{ color: 'var(--text-3)' }}>Email:</span> {viewingMember.email || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span style={{ color: 'var(--text-3)' }}>Phone:</span> <span className="num">{viewingMember.contact || 'N/A'}</span>
                      </p>
                      <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Birth Date:</span> {viewingMember.dob || 'N/A'}
                        </p>
                    </div>
                  </div>

                  <div className="panel" style={{ padding: 'var(--sp-4)' }}>
                    <h5 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
                      <FontAwesomeIcon icon={faGraduationCap} />
                      Education
                    </h5>
                    <div className="space-y-2">
                      <p className="text-sm">
                        <span style={{ color: 'var(--text-3)' }}>Faculty:</span> {viewingMember.faculty || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span style={{ color: 'var(--text-3)' }}>Year:</span> {viewingMember.year || 'N/A'} S{viewingMember.semester || 'N/A'}
                      </p>
                      <p className="text-sm">
                        <span style={{ color: 'var(--text-3)' }}>Center:</span> {viewingMember.center || 'N/A'}
                      </p>
                      <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>School:</span> {viewingMember.school || 'N/A'}
                        </p>
                        <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Skills:</span> {viewingMember.skills || 'N/A'}
                        </p>
                    </div>
                  </div>

                  <div className="panel md:col-span-2" style={{ padding: 'var(--sp-4)' }}>
                    <h5 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
                      <FontAwesomeIcon icon={faUser} />
                      Additional Information
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Position:</span> {viewingMember.position || 'Member'}
                        </p>
                        <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Status:</span> {viewingMember.status || 'Active'}
                        </p>
                        <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Address:</span> {viewingMember.address || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Age:</span> {viewingMember.dob ? calculateAge(viewingMember.dob) : 'N/A'}
                        </p>
                        <p className="text-sm">
                          <span style={{ color: 'var(--text-3)' }}>Gender:</span> {viewingMember.gender || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-foot">
            <button
              onClick={closeModal}
              className="btn btn-ghost"
            >
              Close
            </button>
            <a
              href={`https://wa.me/+94${viewingMember.contact ? viewingMember.contact.replace(/\D/g, '') : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ background: 'var(--success)' }}
            >
              <FontAwesomeIcon icon={faPhone} style={{ fontSize: 12 }} />
              Message on WhatsApp
            </a>
          </div>
        </div>
      </div>
    )}
      </main>
    </div>
  );
}

export default MemberDatabase;
